import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { useLockViewportScroll } from "../../../../hook/useLockViewportScroll";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import enhancer, {
  insuranceVerificationValidationSchema,
  isInsuranceVerificationFormComplete,
} from "./RegistrationInsuranceVerification.validator";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  getRegistrationData,
  updateRegistrationData,
} from "../../registrationSetupStorage";
import {
  INSURANCE_PROVIDER_PLACEHOLDER,
  buildInsuranceManualFromVerification,
  fetchRegistrationInsuranceProviders,
  getInsuranceOptionLabel,
  RELATIONSHIP_OPTIONS,
  formatDobForDateInput,
  normalizeVerificationRelationship,
} from "../insuranceProviders";
import {
  DOB_FORMAT_VALIDATION_KEY,
  resolveRegisterValidationMessage,
} from "../../../Register/Register.validator";
import {
  getInsuranceCardImages,
  hasRequiredInsuranceCardImages,
  setInsuranceCardImages,
} from "../insuranceCardImagesStore";
import RegistrationInsuranceCardUpload from "../RegistrationInsuranceCardUpload";
import RegistrationFormSelect from "../../../RegistrationLayout/RegistrationFormSelect";
import RegistrationDobField from "../../../RegistrationLayout/RegistrationDobField";
import {
  clearInsuranceDetailsReturnToCoverage,
  registrationStepBack,
} from "../../registrationNavigation";

const INSURANCE_STEP = REGISTRATION_SETUP_STEP_META.INSURANCE_VERIFICATION;

const RegistrationInsuranceVerification = (props) => {
  const { history } = props;
  const { t, i18n } = useTranslation();
  const [insuranceProviders, setInsuranceProviders] = useState([
    INSURANCE_PROVIDER_PLACEHOLDER,
  ]);
  const [insuranceProvidersLoading, setInsuranceProvidersLoading] =
    useState(true);
  const [hasCardImages, setHasCardImages] = useState(() =>
    hasRequiredInsuranceCardImages()
  );
  const {
    setFieldValue,
    setFieldTouched,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    submitCount,
    setTouched,
    setErrors,
    isSubmitting,
  } = props;

  useLockViewportScroll();

  useEffect(() => {
    const parsed = getRegistrationData();
    if (!parsed.firstName || !parsed.email) {
      history.replace("/register/account");
      return;
    }
    if (!parsed.dob) {
      history.replace("/register/account");
      return;
    }
    if (parsed.paymentType && parsed.paymentType !== "insurance") {
      history.replace("/register/account/payment");
      return;
    }

    if (parsed.insuranceVerification) {
      const verification = parsed.insuranceVerification;
      if (verification.payer) setFieldValue("payer", verification.payer);
      if (verification.memberId) {
        setFieldValue("memberId", verification.memberId);
      }
      if (verification.groupNumber != null) {
        setFieldValue("groupNumber", verification.groupNumber);
      }
      const relationship = normalizeVerificationRelationship(
        verification.relationship
      );
      if (relationship) {
        setFieldValue("relationship", relationship);
      }

      if (relationship === "self") {
        setFieldValue("firstName", parsed.firstName || "");
        setFieldValue("lastName", parsed.lastName || "");
        setFieldValue("dob", formatDobForDateInput(parsed.dob));
        setFieldValue("policyHolderFirstName", "");
        setFieldValue("policyHolderLastName", "");
        setFieldValue("policyHolderDob", "");
      } else if (relationship) {
        const hasPolicyHolderFields = Boolean(
          verification.policyHolderFirstName ||
            verification.policyHolderLastName ||
            verification.policyHolderDob
        );

        if (hasPolicyHolderFields) {
          setFieldValue(
            "firstName",
            verification.firstName || parsed.firstName || ""
          );
          setFieldValue(
            "lastName",
            verification.lastName || parsed.lastName || ""
          );
          setFieldValue(
            "dob",
            formatDobForDateInput(verification.dob || parsed.dob)
          );
          setFieldValue(
            "policyHolderFirstName",
            verification.policyHolderFirstName || ""
          );
          setFieldValue(
            "policyHolderLastName",
            verification.policyHolderLastName || ""
          );
          setFieldValue(
            "policyHolderDob",
            formatDobForDateInput(verification.policyHolderDob)
          );
        } else {
          // Legacy: firstName/lastName/dob stored the policy holder.
          setFieldValue("firstName", parsed.firstName || "");
          setFieldValue("lastName", parsed.lastName || "");
          setFieldValue("dob", formatDobForDateInput(parsed.dob));
          setFieldValue("policyHolderFirstName", verification.firstName || "");
          setFieldValue("policyHolderLastName", verification.lastName || "");
          setFieldValue(
            "policyHolderDob",
            formatDobForDateInput(verification.dob)
          );
        }
      }
    } else {
      setFieldValue("firstName", parsed.firstName || "");
      setFieldValue("lastName", parsed.lastName || "");
      setFieldValue("dob", formatDobForDateInput(parsed.dob));
      setFieldValue("relationship", "self");
      setFieldValue("policyHolderFirstName", "");
      setFieldValue("policyHolderLastName", "");
      setFieldValue("policyHolderDob", "");
    }

    if (Array.isArray(parsed.insuranceProviderOptions)) {
      setInsuranceProviders(parsed.insuranceProviderOptions);
    }
  }, [history, setFieldValue]);

  useEffect(() => {
    const parsed = getRegistrationData();
    const registrationCode = parsed.registrationCode?.trim();

    if (!registrationCode) {
      history.replace("/register/account");
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    setInsuranceProvidersLoading(true);

    fetchRegistrationInsuranceProviders({
      registrationCode,
      language: i18n.language,
      apiService: AuthenticationApiSerivce,
      signal: controller.signal,
    })
      .then(({ list, options }) => {
        if (cancelled) {
          return;
        }

        setInsuranceProviders(options);
        updateRegistrationData({
          insuranceProviderOptions: options,
          insuranceListItems: list,
        });
      })
      .catch((err) => {
        if (
          cancelled ||
          isRequestCancelledError(err) ||
          isRegistrationLoadRetryExhaustedError(err)
        ) {
          return;
        }
        message.error(
          err?.message?.startsWith?.("NUTRITION_")
            ? t(err.message)
            : err?.message || t("NUTRITION_INSDISC_ERR_FETCH_LIST")
        );
      })
      .finally(() => {
        if (!cancelled) {
          setInsuranceProvidersLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [history, i18n.language, t]);

  const showError = (field) => {
    const dobFieldValue = String(values[field] || "").trim();
    const isDobField = field === "dob" || field === "policyHolderDob";
    const isTypingPartialDob =
      isDobField &&
      dobFieldValue.length > 0 &&
      dobFieldValue.length < 10 &&
      /^[\d/]+$/.test(dobFieldValue);
    const hasFormatErrorOnRenderedDob =
      isDobField &&
      Boolean(dobFieldValue) &&
      !isTypingPartialDob &&
      errors[field] === DOB_FORMAT_VALIDATION_KEY;

    if (
      (errors[field] && touched[field]) ||
      submitCount > 0 ||
      hasFormatErrorOnRenderedDob
    ) {
      return (
        <div className="text-danger">
          {resolveRegisterValidationMessage(errors[field], t)}
        </div>
      );
    }
    return null;
  };

  const showPolicyHolderFields =
    values.relationship === "spouse" || values.relationship === "dependent";

  const handleRelationshipChange = (e) => {
    const relationship = e.target.value;
    handleChange(e);

    const accountData = getRegistrationData();
    if (relationship === "self") {
      setFieldValue("firstName", accountData.firstName || "");
      setFieldValue("lastName", accountData.lastName || "");
      setFieldValue("dob", formatDobForDateInput(accountData.dob));
      setFieldValue("policyHolderFirstName", "");
      setFieldValue("policyHolderLastName", "");
      setFieldValue("policyHolderDob", "");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      payer: true,
      firstName: true,
      lastName: true,
      dob: true,
      memberId: true,
      groupNumber: true,
      relationship: true,
      policyHolderFirstName: true,
      policyHolderLastName: true,
      policyHolderDob: true,
    });

    try {
      insuranceVerificationValidationSchema.validateSync(values, {
        abortEarly: false,
      });
      setErrors({});
    } catch (validationError) {
      if (validationError.inner) {
        const formErrors = {};
        validationError.inner.forEach((error) => {
          if (error.path) {
            formErrors[error.path] = error.message;
          }
        });
        setErrors(formErrors);
      }
      return;
    }

    if (!hasRequiredInsuranceCardImages()) {
      return;
    }

    const registrationData = getRegistrationData();
    if (!registrationData.dob) {
      history.replace("/register/account");
      return;
    }

    setInsuranceCardImages(getInsuranceCardImages());

    const isSelf = values.relationship === "self";
    const insuranceVerification = {
      payer: values.payer,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      dob: values.dob,
      memberId: values.memberId.trim(),
      groupNumber: values.groupNumber.trim(),
      relationship: values.relationship,
      policyHolderFirstName: isSelf
        ? ""
        : String(values.policyHolderFirstName ?? "").trim(),
      policyHolderLastName: isSelf
        ? ""
        : String(values.policyHolderLastName ?? "").trim(),
      policyHolderDob: isSelf ? "" : values.policyHolderDob,
    };

    updateRegistrationData({
      insuranceVerification,
      // Keep manual in sync so a later details visit does not revive a stale DOB.
      insuranceManual: buildInsuranceManualFromVerification(insuranceVerification),
      paymentType: "insurance",
      insuranceRecheck: false,
      insuranceEligibilityResult: undefined,
      coverageResult: undefined,
    });

    history.push("/register/account/insurance/checking");
  };

  const handleManualEntry = () => {
    clearInsuranceDetailsReturnToCoverage();

    const isSelf = values.relationship === "self";
    const insuranceVerification = {
      payer: values.payer,
      firstName: String(values.firstName ?? "").trim(),
      lastName: String(values.lastName ?? "").trim(),
      dob: values.dob,
      memberId: String(values.memberId ?? "").trim(),
      groupNumber: String(values.groupNumber ?? "").trim(),
      relationship: values.relationship,
      policyHolderFirstName: isSelf
        ? ""
        : String(values.policyHolderFirstName ?? "").trim(),
      policyHolderLastName: isSelf
        ? ""
        : String(values.policyHolderLastName ?? "").trim(),
      policyHolderDob: isSelf ? "" : values.policyHolderDob,
    };

    updateRegistrationData({
      insuranceVerification,
      insuranceManual: buildInsuranceManualFromVerification(insuranceVerification),
      paymentType: "insurance",
      insuranceRecheck: false,
    });

    history.push("/register/account/insurance/details");
  };

  const handleBack = () => {
    registrationStepBack(history);
  };

  const canCheckCoverage =
    isInsuranceVerificationFormComplete(values) &&
    hasCardImages &&
    !isSubmitting &&
    !insuranceProvidersLoading;

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={INSURANCE_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={handleBack}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card">
            <form className="content-card-scroll" onSubmit={onSubmit} noValidate>
              <div className="insurance-hero">
                <div className="insurance-hero-icon" aria-hidden>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8aab15"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <polyline points="9 12 11 14 15 10" />
                  </svg>
                </div>
                <div>
                  <h2 className="insurance-hero-title">
                    {t("NUTRITION_INSDISC_LBL_HEADER")}
                  </h2>
                </div>
              </div>

              <p className="section-sub">
                {t("NUTRITION_INSDISC_DESC_SUBTITLE")}
              </p>

              <div className="insurance-details-grid">
                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="insurance-payer">
                    {t("NUTRITION_COMMON_LBL_INSURANCE_PROVIDER")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <RegistrationFormSelect
                    id="insurance-payer"
                    name="payer"
                    value={values.payer}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={insuranceProvidersLoading}
                  >
                    {insuranceProviders.map((p) => (
                      <option key={p.value || "placeholder"} value={p.value}>
                        {getInsuranceOptionLabel(p, t)}
                      </option>
                    ))}
                  </RegistrationFormSelect>
                  {showError("payer")}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="insurance-first">
                    {t("NUTRITION_INSDISC_LBL_FIRST_NAME")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <input
                    id="insurance-first"
                    name="firstName"
                    type="text"
                    className="form-input"
                    placeholder={t("NUTRITION_INSDISC_PLACEHOLDER_FIRST_NAME")}
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {showError("firstName")}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="insurance-last">
                    {t("NUTRITION_INSDISC_LBL_LAST_NAME")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <input
                    id="insurance-last"
                    name="lastName"
                    type="text"
                    className="form-input"
                    placeholder={t("NUTRITION_INSDISC_PLACEHOLDER_LAST_NAME")}
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {showError("lastName")}
                </div>

                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="insurance-dob">
                    {t("DATE_OF_BIRTH")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <RegistrationDobField
                    id="insurance-dob"
                    name="dob"
                    value={values.dob}
                    onChange={(formatted, { touched }) => {
                      if (touched) {
                        setFieldTouched("dob", true);
                      }
                      setFieldValue("dob", formatted);
                    }}
                    onBlur={handleBlur}
                  />
                  {showError("dob")}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="insurance-member-id">
                    {t("MEMBER_ID")} <span className="required-mark">*</span>
                  </label>
                  <input
                    id="insurance-member-id"
                    name="memberId"
                    type="text"
                    className="form-input"
                    placeholder={t("NUTRITION_INSDISC_HINT_MEMBER_ID_CARD")}
                    value={values.memberId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {showError("memberId")}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="insurance-group-number">
                    {t("NUTRITION_COMMON_LBL_GROUP_NUMBER")}
                  </label>
                  <input
                    id="insurance-group-number"
                    name="groupNumber"
                    type="text"
                    className="form-input"
                    placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_GROUP_NUMBER")}
                    value={values.groupNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {showError("groupNumber")}
                </div>

                <div className="form-group insurance-details-span-2">
                  <label className="form-label" htmlFor="insurance-relationship">
                    {t("NUTRITION_INSDISC_LBL_RELATIONSHIP")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <RegistrationFormSelect
                    id="insurance-relationship"
                    name="relationship"
                    value={values.relationship}
                    onChange={handleRelationshipChange}
                    onBlur={handleBlur}
                  >
                    {RELATIONSHIP_OPTIONS.map((opt) => (
                      <option key={opt.value || "placeholder"} value={opt.value}>
                        {getInsuranceOptionLabel(opt, t)}
                      </option>
                    ))}
                  </RegistrationFormSelect>
                  {showError("relationship")}
                </div>

                {showPolicyHolderFields && (
                  <>
                    <div className="form-group">
                      <label
                        className="form-label"
                        htmlFor="insurance-policy-holder-first"
                      >
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_FIRST_NAME")}{" "}
                        <span className="required-mark">*</span>
                      </label>
                      <input
                        id="insurance-policy-holder-first"
                        name="policyHolderFirstName"
                        type="text"
                        className="form-input"
                        placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_FIRST_NAME")}
                        value={values.policyHolderFirstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {showError("policyHolderFirstName")}
                    </div>

                    <div className="form-group">
                      <label
                        className="form-label"
                        htmlFor="insurance-policy-holder-last"
                      >
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_LAST_NAME")}{" "}
                        <span className="required-mark">*</span>
                      </label>
                      <input
                        id="insurance-policy-holder-last"
                        name="policyHolderLastName"
                        type="text"
                        className="form-input"
                        placeholder={t("NUTRITION_INSMANUAL_PLACEHOLDER_LAST_NAME")}
                        value={values.policyHolderLastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {showError("policyHolderLastName")}
                    </div>

                    <div className="form-group insurance-details-span-2">
                      <label
                        className="form-label"
                        htmlFor="insurance-policy-holder-dob"
                      >
                        {t("NUTRITION_INSMANUAL_LBL_HOLDER_DOB")}{" "}
                        <span className="required-mark">*</span>
                      </label>
                      <RegistrationDobField
                        id="insurance-policy-holder-dob"
                        name="policyHolderDob"
                        value={values.policyHolderDob}
                        onChange={(formatted, { touched: dobTouched }) => {
                          if (dobTouched) {
                            setFieldTouched("policyHolderDob", true);
                          }
                          setFieldValue("policyHolderDob", formatted);
                        }}
                        onBlur={handleBlur}
                      />
                      {showError("policyHolderDob")}
                    </div>
                  </>
                )}

                <RegistrationInsuranceCardUpload
                  forceShowErrors={submitCount > 0}
                  idPrefix="verify-insurance-card"
                  onImagesChange={setHasCardImages}
                />
              </div>

              <div className="divider" />

              <div className="notice-box notice-box--coverage">
                <div className="notice-icon">🔒</div>
                <div className="notice-text">
                  {t("NUTRITION_INSDISC_DESC_NOTICE")}
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={!canCheckCoverage}
              >
                {t("NUTRITION_INSDISC_BTN_CHECK")}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={handleManualEntry}
              >
                {t("NUTRITION_INSDISC_BTN_MANUAL")}
              </button>
            </form>
          </div>
        </div>
      </RegistrationLayout>
    </RegistrationSetupWrapper>
  );
};

export default compose(
  withRouter,
  enhancer
)(RegistrationInsuranceVerification);
